import classNames from "classnames";
import Link from "next/link";
import * as React from "react";
import { AppLinkSection } from "@components/AppLinkSection";
import { getImageFluxUrl } from "@components/ImageFlux";
import matchListItemStyles from "@components/MatchList/index.module.css";
import useAccessFromApp from "@hooks/useAccessFromApp";
import useIsPcByUA from "@hooks/useIsPcByUA";
import { paths } from "@libs/paths";
import { IFImg } from "@libs/utils/IFImg";
import { toJpDate, toJpTime } from "@libs/utils/date";
import { createGoogleMapAppUrl } from "@libs/utils/map";
import { shouldShowMatchTime } from "@libs/utils/match";
import { launchTel } from "@libs/utils/url";
import { useCampaignTemplate_CampaignMatchesQuery as useCampaignMatchesQuery } from "./__generated__/queries";
import styles from "./index.module.css";
import type { CampaignTemplate_CampaignShopFragment } from "./__generated__/fragments";

export type ClubCampaignPrefectureGroup = {
  prefecture: string;
  shopUuids: Array<string>;
};

type Props = {
  teamName: string;
  slug: string;
  leagueName?: string;
  teamIds: Array<number>;
  colorText: string;
  prefectures?: Array<ClubCampaignPrefectureGroup>;
  shopData?: Array<CampaignTemplate_CampaignShopFragment>;
  customStyle: CampaignTemplateCustomStyle;
  presentAreaHidden?: boolean;
  specialEventAreaHidden?: boolean;
  sportGenreSlug?: string;
  teamSlug?: string;
};

export type CampaignTemplateCustomStyle = {
  themeColorFirst: string;
  themeColorSecond: string;
  specialEventSectionTitle?: string;
  leadSectionTitle?: string;
  shopSectionTitle?: string;
  presentSectionTitle?: string;
  attentionSectionTitle: string;
  shopsAttentionColor?: string;
  appLinkSection: string;
  fanstaShop: string;
  shopsShopButton: string;
  shopsModalButton: string;
};

const CampaignTemplate: React.FC<Props> = ({
  teamName,
  slug,
  // TODO: teamIdからteamName, leagueName取得できるAPIがあると別々に渡さなくて済む
  leagueName,
  teamIds,
  colorText,
  prefectures,
  shopData,
  customStyle,
  presentAreaHidden,
  specialEventAreaHidden,
  sportGenreSlug,
  teamSlug,
}) => {
  const matchesData = useCampaignMatchesQuery({
    variables: { teamIds: teamIds, limit: 6 },
  });
  const drinkOrFood = teamName === "川崎フロンターレ" ? "フード" : "ドリンク";
  const titlePath =
    teamName === "横浜F・マリノス" ? "fmarinos-202203" : "common";

  // 店舗一覧ページに遷移する時のクエリパラメータ
  // UIの設定のみに利用され、実際の絞り込みには利用されないので注意
  const shopQueryParams = { prefectureId: 13 };

  const fromApp = useAccessFromApp();
  const generateToShopsUrl = (): string => {
    return !fromApp && sportGenreSlug && teamSlug
      ? paths.staticShops({
          genreSlug: `s-${sportGenreSlug}`,
          teamSlug: teamSlug,
        })
      : paths.shops({
          ...shopQueryParams,
          teamIds: teamIds,
        });
  };

  const { isPcByUA } = useIsPcByUA.useContainer();
  const shouldUseBlack = teamName === "名古屋グランパス";

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.closeLabel}>
          <span>※このキャンペーンは終了しました</span>
        </div>
        <div className={styles.jumbotron}>
          <h1>
            <span className="md:none">
              <img
                src={IFImg(`/img/campaign/${slug}/sp/jumbotron.png`)}
                width={750}
                height={1130}
                alt={`スポーツライブビューイング2022 ${teamName}をお店で応援しよう`}
              />
            </span>
            <span className="none md:block">
              <img
                src={IFImg(`/img/campaign/${slug}/pc/jumbotron.png`)}
                width={2560}
                height={1316}
                alt={`スポーツライブビューイング2022 ${teamName}をお店で応援しよう`}
              />
            </span>
          </h1>
          <div className={styles.scroll}>
            <img
              src={IFImg("/img/campaign/common/scroll.svg")}
              width={21}
              height={160}
              alt={"SCROLL"}
            />
          </div>
        </div>
        {!specialEventAreaHidden && (
          <section className={styles.specialEvent}>
            <div className={styles.container}>
              <h2
                className={classNames(
                  styles.sectionTitle,
                  customStyle.specialEventSectionTitle
                )}
              >
                <span>
                  <i className={styles.sectionTitleLabel}>
                    <img
                      src={IFImg(`/img/campaign/common/spevent-title.svg`)}
                      width={126}
                      height={25}
                      alt="SPECIAL EVENT"
                    />
                  </i>
                  <i className={styles.sectionTitleDAZN}>
                    <img
                      className={styles.sectionTitleDAZNLogo}
                      src="/img/logo_dazn.svg"
                      alt="DAZN"
                    />
                    Organized by DAZN
                  </i>
                </span>
                <em>
                  アウェイ戦に行けないときは
                  <br className="md:none" />
                  お店で応援しよう！
                </em>
              </h2>

              {teamName === "横浜F・マリノス" && (
                <>
                  <div className={styles.specialEventBox}>
                    <div className={styles.specialEventThumbnail}>
                      <span>
                        <img
                          src={IFImg(
                            `/img/campaign/fmarinos-202203/spevent/event_2210_01.png`
                          )}
                          width={354}
                          height={354}
                          alt="清水範久さん・天野貴史さんと一緒にお店で観戦！ 11/5SAT vs ヴィッセル神戸[アウェイ戦] 14:00 KICK OFF @HUBリッチモンドホテル横浜駅前店"
                        />
                      </span>
                    </div>
                    <dl className={styles.specialEventInfo}>
                      <dt className={styles.specialEventInfoTitle}>
                        清水範久さん・天野貴史さんと一緒にお店で観戦！
                        @HUBリッチモンドホテル横浜駅前店
                        <div className={styles.date}>2022/11/5 (土) 14:00</div>
                        <div className={styles.match}>
                          <div className={styles.matchCard}>
                            <div className={styles.team}>
                              <div className={styles.teamLogo}>
                                <img
                                  src={"/img/team_icon/vissel_kobe.png"}
                                  width={36}
                                  height={36}
                                  alt=""
                                />
                              </div>
                              <div className={styles.teamName}>神戸</div>
                            </div>
                            <span className={styles.vs}>vs</span>
                            <div className={styles.team}>
                              <div className={styles.teamLogo}>
                                <img
                                  src={"/img/team_icon/yokohama_f_marinos.png"}
                                  width={36}
                                  height={36}
                                  alt=""
                                />
                              </div>
                              <div className={styles.teamName}>横浜FM</div>
                            </div>
                          </div>
                          <div className={styles.copyDAZN}>
                            Organized by DAZN
                          </div>
                        </div>
                      </dt>
                      <dd className={styles.specialEventInfoContent}>
                        清水範久さん・天野貴史さんがHUBリッチモンドホテル横浜駅前店にライブビューイングゲストとして登場、アウェイ戦を一緒に観戦しよう。ご来店の際には確実にお席を確保できるネット予約をおすすめします。
                      </dd>
                      <dd className={styles.specialEventInfoNotice}>
                        ※奇数名での予約の場合、ご相席をお願いする可能性があります。
                        <br />
                        ※店舗ページから対象試合を選択し予約してください。予約申し込み後にお店が承認すると成立するリクエスト予約になります。
                        <br />
                        ※店内の模様(写真や映像)は雑誌・テレビ等の取材、インターネットでの記事・動画・SNS発信等に利用させていただく可能性があります。
                      </dd>
                      <dd className={styles.specialEventInfoButton}>
                        <a
                          href={paths.shopDetail(
                            shopData?.find((s) => s.uuid === "aFiCDDjQfsdW")
                              ? "aFiCDDjQfsdW"
                              : "UuidUuidUuid"
                          )}
                          className={styles.button}
                          target="_blank"
                        >
                          予約はこちら
                        </a>
                      </dd>
                    </dl>
                  </div>

                  <div className={styles.specialEventBox}>
                    <div className={styles.specialEventThumbnail}>
                      <span>
                        <img
                          src={IFImg(
                            `/img/campaign/fmarinos-202203/spevent/event_2210_02.png`
                          )}
                          width={354}
                          height={354}
                          alt="兵藤慎剛さん・小椋祥平さんと一緒にお店で観戦！ 11/5SAT vs ヴィッセル神戸[アウェイ戦] 14:00 KICK OFF @HUB日比谷店"
                        />
                      </span>
                    </div>
                    <dl className={styles.specialEventInfo}>
                      <dt className={styles.specialEventInfoTitle}>
                        兵藤慎剛さん・小椋祥平さんと一緒にお店で観戦！
                        @HUB日比谷店
                        <div className={styles.date}>2022/11/5 (土) 14:00</div>
                        <div className={styles.match}>
                          <div className={styles.matchCard}>
                            <div className={styles.team}>
                              <div className={styles.teamLogo}>
                                <img
                                  src={"/img/team_icon/vissel_kobe.png"}
                                  width={36}
                                  height={36}
                                  alt=""
                                />
                              </div>
                              <div className={styles.teamName}>神戸</div>
                            </div>
                            <span className={styles.vs}>vs</span>
                            <div className={styles.team}>
                              <div className={styles.teamLogo}>
                                <img
                                  src={"/img/team_icon/yokohama_f_marinos.png"}
                                  width={36}
                                  height={36}
                                  alt=""
                                />
                              </div>
                              <div className={styles.teamName}>横浜FM</div>
                            </div>
                          </div>
                          <div className={styles.copyDAZN}>
                            Organized by DAZN
                          </div>
                        </div>
                      </dt>
                      <dd className={styles.specialEventInfoContent}>
                        兵藤慎剛さん・小椋祥平さんがHUB日比谷店にライブビューイングゲストとして登場、アウェイ戦を一緒に観戦しよう。ご来店の際には確実にお席を確保できるネット予約をおすすめします。
                      </dd>
                      <dd className={styles.specialEventInfoNotice}>
                        ※奇数名での予約の場合、ご相席をお願いする可能性があります。
                        <br />
                        ※店舗ページから対象試合を選択し予約してください。予約申し込み後にお店が承認すると成立するリクエスト予約になります。
                        <br />
                        ※店内の模様(写真や映像)は雑誌・テレビ等の取材、インターネットでの記事・動画・SNS発信等に利用させていただく可能性があります。
                      </dd>
                      <dd className={styles.specialEventInfoButton}>
                        <a
                          href={paths.shopDetail(
                            shopData?.find((s) => s.uuid === "n3uvA2JhwPlL")
                              ? "n3uvA2JhwPlL"
                              : "UuidUuidUuid"
                          )}
                          className={styles.button}
                          target="_blank"
                        >
                          予約はこちら
                        </a>
                      </dd>
                    </dl>
                  </div>

                  <div className={styles.specialEventBox}>
                    <div className={styles.specialEventThumbnail}>
                      <span>
                        <img
                          src={IFImg(
                            `/img/campaign/fmarinos-202203/spevent/event_2208_03.png`
                          )}
                          width={354}
                          height={354}
                          alt="アウェイ戦日にFanstaアプリからの観戦予約で全員にクリアケースをプレゼント！11/5SAT vs ヴィッセル神戸[アウェイ戦] 14:00 KICK OFF @HUB新横浜店"
                        />
                      </span>
                    </div>
                    <dl className={styles.specialEventInfo}>
                      <dt className={styles.specialEventInfoTitle}>
                        アウェイ戦日にFanstaアプリからの観戦予約で全員にクリアケースをプレゼント！
                      </dt>
                      <dd className={styles.specialEventInfoContent}>
                        対象試合において下記対象店舗の観戦予約をされた方に試合当日、店舗でオリジナルクリアケースをプレゼントします。
                        <br />
                        [対象試合]
                        <br />
                        <p className={styles.specialEventMatchTable}>
                          <span>11月5日(土)</span>
                          <span className="team">vsヴィッセル神戸</span>
                          <span>14:00 KICK OFF</span>
                        </p>
                        [対象店舗]
                        <br />
                        ・HUB新横浜店
                        <br />
                        ・HUBリッチモンドホテル横浜駅前店
                        <br />
                        ・家庭料理とワインのお店 レストア
                        <br />
                        ・HUB渋谷3号店
                        <br />
                        ・HUB日比谷店
                        <br />
                      </dd>
                      <dd className={styles.specialEventInfoNotice}>
                        ※Fanstaアプリからの予約1件に対して必ず1個プレゼント致します。
                        <br />
                        ※予約者本人以外の方はアプリDL・お気に入りチーム登録で予約人数全員にプレゼント致します。
                        <br />
                        ※店舗予約ページから対象試合を選択し予約してください。予約申し込み後にお店が承認すると成立するリクエスト予約になります。
                      </dd>
                      <dd className={styles.specialEventInfoButton}>
                        <a href="#shops" className={styles.button}>
                          対象店舗はこちら
                        </a>
                      </dd>
                    </dl>
                  </div>
                </>
              )}

              {teamName === "FC東京" && (
                <>
                  <div className={styles.specialEventBox}>
                    <div className={styles.specialEventThumbnail}>
                      <span>
                        <img
                          src={IFImg(
                            `/img/campaign/fctokyo-202203/spevent/event_2210_01.png`
                          )}
                          width={354}
                          height={354}
                          alt="アマラオさん・小峯隆幸さんと一緒にお店で観戦！ 10/29SAT vs 名古屋グランパス[アウェイ戦] 15:00 KICK OFF @HUBチョムチョム秋葉原店"
                        />
                      </span>
                    </div>
                    <dl className={styles.specialEventInfo}>
                      <dt className={styles.specialEventInfoTitle}>
                        アマラオさん・小峯隆幸さんと一緒にお店で観戦！
                        <br className={styles.onlyDesktop} />
                        @HUBチョムチョム秋葉原店
                        <div className={styles.date}>2022/10/29 (土) 15:00</div>
                        <div className={styles.match}>
                          <div className={styles.matchCard}>
                            <div className={styles.team}>
                              <div className={styles.teamLogo}>
                                <img
                                  src={"/img/team_icon/nagoya_grampus.png"}
                                  width={36}
                                  height={36}
                                  alt=""
                                />
                              </div>
                              <div className={styles.teamName}>名古屋</div>
                            </div>
                            <span className={styles.vs}>vs</span>
                            <div className={styles.team}>
                              <div className={styles.teamLogo}>
                                <img
                                  src={"/img/team_icon/fctokyo.png"}
                                  width={36}
                                  height={36}
                                  alt=""
                                />
                              </div>
                              <div className={styles.teamName}>FC東京</div>
                            </div>
                          </div>
                          <div className={styles.copyDAZN}>
                            Organized by DAZN
                          </div>
                        </div>
                      </dt>
                      <dd className={styles.specialEventInfoContent}>
                        アマラオさん・小峯隆幸さんがHUBチョムチョム秋葉原店にライブビューイングゲストとして登場、アウェイ戦を一緒に観戦しよう。ご来店の際には確実にお席を確保できるネット予約をおすすめします。
                      </dd>
                      <dd className={styles.specialEventInfoNotice}>
                        ※店舗ページから対象試合を選択し予約してください。予約申し込み後にお店が承認すると成立するリクエスト予約になります。
                        <br />
                        ※店内の模様(写真や映像)は雑誌・テレビ等の取材、インターネットでの記事・動画・SNS発信等に利用させていただく可能性があります。
                      </dd>
                      <dd className={styles.specialEventInfoButton}>
                        <a
                          href={paths.shopDetail(
                            shopData?.find((s) => s.uuid === "TcfSDsXxaCSU")
                              ? "TcfSDsXxaCSU"
                              : "UuidUuidUuid"
                          )}
                          className={styles.button}
                          target="_blank"
                        >
                          予約はこちら
                        </a>
                      </dd>
                    </dl>
                  </div>

                  <div className={styles.specialEventBox}>
                    <div className={styles.specialEventThumbnail}>
                      <span>
                        <img
                          src={IFImg(
                            `/img/campaign/fctokyo-202203/spevent/event_2210_02.png`
                          )}
                          width={354}
                          height={354}
                          alt="コハロンさんと一緒にお店で観戦！ 10/29SAT vs 名古屋グランパス[アウェイ戦] 15:00 KICK OFF @HUB渋谷店"
                        />
                      </span>
                    </div>
                    <dl className={styles.specialEventInfo}>
                      <dt className={styles.specialEventInfoTitle}>
                        コハロンさんと一緒にお店で観戦！@HUB渋谷店
                        <div className={styles.date}>2022/10/29 (土) 15:00</div>
                        <div className={styles.match}>
                          <div className={styles.matchCard}>
                            <div className={styles.team}>
                              <div className={styles.teamLogo}>
                                <img
                                  src={"/img/team_icon/nagoya_grampus.png"}
                                  width={36}
                                  height={36}
                                  alt=""
                                />
                              </div>
                              <div className={styles.teamName}>名古屋</div>
                            </div>
                            <span className={styles.vs}>vs</span>
                            <div className={styles.team}>
                              <div className={styles.teamLogo}>
                                <img
                                  src={"/img/team_icon/fctokyo.png"}
                                  width={36}
                                  height={36}
                                  alt=""
                                />
                              </div>
                              <div className={styles.teamName}>FC東京</div>
                            </div>
                          </div>
                          <div className={styles.copyDAZN}>
                            Organized by DAZN
                          </div>
                        </div>
                      </dt>
                      <dd className={styles.specialEventInfoContent}>
                        FC東京応援YoutuberのコハロンさんがHUB渋谷店にライブビューイングゲストとして登場、アウェイ戦を一緒に観戦しよう。ご来店の際には確実にお席を確保できるネット予約をおすすめします。
                      </dd>
                      <dd className={styles.specialEventInfoNotice}>
                        ※店舗ページから対象試合を選択し予約してください。予約申し込み後にお店が承認すると成立するリクエスト予約になります。
                        <br />
                        ※店内の模様(写真や映像)は雑誌・テレビ等の取材、インターネットでの記事・動画・SNS発信等に利用させていただく可能性があります。
                      </dd>
                      <dd className={styles.specialEventInfoButton}>
                        <a
                          href={paths.shopDetail(
                            shopData?.find((s) => s.uuid === "Mz3mi2fG9nT2")
                              ? "Mz3mi2fG9nT2"
                              : "UuidUuidUuid"
                          )}
                          className={styles.button}
                          target="_blank"
                        >
                          予約はこちら
                        </a>
                      </dd>
                    </dl>
                  </div>

                  <div className={styles.specialEventBox}>
                    <div className={styles.specialEventThumbnail}>
                      <span>
                        <img
                          src={IFImg(
                            `/img/campaign/fctokyo-202203/spevent/event_2206_02.png`
                          )}
                          width={354}
                          height={354}
                          alt="Fansta アウェイ戦ネット予約者限定！！Fanstaで対象店舗のネット予約をすると抽選で選手サイン入り色紙が当たる！！"
                        />
                      </span>
                    </div>
                    <dl className={styles.specialEventInfo}>
                      <dt className={styles.specialEventInfoTitle}>
                        アウェイ戦をFanstaでネット予約すると抽選でサイン入り色紙が当たる！
                      </dt>
                      <dd className={styles.specialEventInfoContent}>
                        2022/10/29(土)
                        名古屋グランパス戦の対象店舗と色紙サイン選手の組み合わせは以下になります。
                        <br />
                        ・HUB吉祥寺店：松木選手、ディエゴ選手、ルイス選手
                        <br />
                        ・HUBチョムチョム秋葉原店：塚川選手、木本選手、アダイウトン選手
                        <br />
                        ・HUB渋谷店：東選手、紺野選手
                        <br />
                        ・HUB新宿西口大ガード店：レアンドロ選手、渡邊選手
                        <br />
                        ・HUB高田馬場店：木本選手、紺野選手
                        <br />
                        ・Cafe Bar LIVRE：東選手、ルイス選手
                        <br />
                        ・IRISH PUB KENNY'S：渡邊選手、アダイウトン選手
                      </dd>
                      <dd className={styles.specialEventInfoNotice}>
                        ※当日店舗で抽選と受渡しを行いますが抽選方法は店舗によって異なります。予約の際に各店舗のプラン内容/注意事項をご確認ください。
                      </dd>
                      <dd className={styles.specialEventInfoButton}>
                        <a href="#shops" className={styles.button}>
                          対象店舗はこちら
                        </a>
                      </dd>
                    </dl>
                  </div>
                  <div className={styles.specialEventBox}>
                    <div className={styles.specialEventThumbnail}>
                      <span>
                        <img
                          src={IFImg(
                            `/img/campaign/fctokyo-202203/spevent/event_2206_01.png`
                          )}
                          width={354}
                          height={354}
                          alt="Fansta アウェイ戦日限定！！ Fansta応援ドリンクを注文すると抽選で選手サイン入りステッカーが当たる！！"
                        />
                      </span>
                    </div>
                    <dl className={styles.specialEventInfo}>
                      <dt className={styles.specialEventInfoTitle}>
                        アウェイ戦日に応援ドリンクを注文すると抽選でサイン入りステッカーが当たる！
                      </dt>
                      <dd className={styles.specialEventInfoContent}>
                        2022/10/29(土)
                        名古屋グランパス戦の対象店舗とステッカーサイン選手の組み合わせは以下になります。
                        <br />
                        ・HUB吉祥寺店：松木選手、木村選手
                        <br />
                        ・HUBチョムチョム秋葉原店：安部選手、品田選手
                        <br />
                        ・HUB渋谷店：アダイウトン選手
                        <br />
                        ・HUB新宿西口大ガード店：紺野選手、梶浦選手
                        <br />
                        ・HUB高田馬場店：安田選手
                        <br />
                        ・Cafe Bar LIVRE：安部選手
                        <br />
                        ・IRISH PUB KENNY'S：塚川選手
                      </dd>
                      <dd className={styles.specialEventInfoNotice}>
                        ※ステッカーが無くなり次第抽選を終了します。
                        <br />
                        ※ステッカーは当日店舗で抽選と受渡しを行います。抽選方法は店舗によって異なります。
                      </dd>
                      <dd className={styles.specialEventInfoButton}>
                        <a href="#shops" className={styles.button}>
                          対象店舗はこちら
                        </a>
                      </dd>
                    </dl>
                  </div>
                </>
              )}

              {teamName === "名古屋グランパス" && (
                <>
                  <div className={styles.specialEventBox}>
                    <div className={styles.specialEventThumbnail}>
                      <span>
                        <img
                          src={IFImg(
                            `/img/campaign/grampus-202209/spevent/event_2211_01.png`
                          )}
                          width={354}
                          height={354}
                          alt="Fansta アウェイ戦ネット予約者限定！！Fanstaで対象店舗のネット予約をすると抽選で選手サイン入り色紙が当たる！！"
                        />
                      </span>
                    </div>
                    <dl className={styles.specialEventInfo}>
                      <dt className={styles.specialEventInfoTitle}>
                        アウェイ戦をFanstaでネット予約すると抽選でサイン入り色紙が当たる！
                      </dt>
                      <dd className={styles.specialEventInfoContent}>
                        2022/11/5(土)
                        セレッソ大阪戦を対象店舗で予約された方の中から各店舗1名様に藤井陽也選手の直筆サイン入り色紙をプレゼント！
                        <br />
                        [対象店舗]
                        <br />
                        ・HUB GRAMPUS PUB MIRAI TOWER店
                        <br />
                        ・HUB名駅四丁目店
                        <br />
                        ・ブービーズ豊田店
                      </dd>
                      <dd className={styles.specialEventInfoNotice}>
                        ※色紙は各店舗1試合につき1枚ずつとなります。当日店舗で抽選と受渡しを行いますが抽選方法は店舗によって異なります。予約の際に各店舗のプラン内容/注意事項をご確認ください。
                      </dd>
                      <dd className={styles.specialEventInfoButton}>
                        <a href="#shops" className={styles.button}>
                          対象店舗の予約はこちら
                        </a>
                      </dd>
                    </dl>
                  </div>
                </>
              )}

              {teamName === "セレッソ大阪" && (
                <>
                  <div className={styles.specialEventBox}>
                    <div className={styles.specialEventThumbnail}>
                      <span>
                        <img
                          src={IFImg(
                            `/img/campaign/cerezo-202203/spevent/event_2209_01.png`
                          )}
                          width={354}
                          height={354}
                          alt="Fansta アウェイ戦ネット予約者限定！！ Fanstaで対象店舗をネット予約すると抽選で毎試合各店舗1名様に選手サイン入りうちわが当たる！！"
                        />
                      </span>
                    </div>
                    <dl className={styles.specialEventInfo}>
                      <dt className={styles.specialEventInfoTitle}>
                        アウェイ戦をFanstaでネット予約すると抽選でサイン入りうちわが当たる！
                        <div className={styles.date}>2022/10/29 (土) 15:00</div>
                        <div className={styles.match}>
                          <div className={styles.matchCard}>
                            <div className={styles.team}>
                              <div className={styles.teamLogo}>
                                <img
                                  src={"/img/team_icon/kyoto_sanga_fc.png"}
                                  width={36}
                                  height={36}
                                  alt=""
                                />
                              </div>
                              <div className={styles.teamName}>
                                京都サンガF.C.
                              </div>
                            </div>
                            <span className={styles.vs}>vs</span>
                            <div className={styles.team}>
                              <div className={styles.teamLogo}>
                                <img
                                  src={"/img/team_icon/cerezo_osaka.png"}
                                  width={36}
                                  height={36}
                                  alt=""
                                />
                              </div>
                              <div className={styles.teamName}>C大阪</div>
                            </div>
                          </div>
                          <div className={styles.copyDAZN}>
                            Organized by DAZN
                          </div>
                        </div>
                      </dt>
                      <dd className={styles.specialEventInfoContent}>
                        2022/10/29(土)
                        京都サンガF.C.戦の対象店舗とうちわサイン選手の組み合わせは以下になります。
                        <br />
                        ・HUB梅田東通り店：山中選手
                        <br />
                        ・HUB心斎橋店：西尾選手
                        <br />
                        ・HUBあべのキューズモール店：加藤選手
                        <br />
                        ・HUB恵比寿店：松田選手
                        <br />
                        ・HUB八重洲店：原川選手
                        <br />
                        ・鶴橋らーめん食堂鶴心：毎熊選手
                        <br />
                        ・Bar Sakuraholic：山田選手
                      </dd>
                      <dd className={styles.specialEventInfoNotice}>
                        ※うちわは各店舗1試合につき1枚ずつとなります。当日店舗で抽選と受渡しを行いますが抽選方法は店舗によって異なります。予約の際に各店舗のプラン内容/注意事項をご確認ください。
                      </dd>
                      <dd className={styles.specialEventInfoButton}>
                        <a href="#shops" className={styles.button}>
                          対象店舗はこちら
                        </a>
                      </dd>
                    </dl>
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {specialEventAreaHidden && (
          <section
            className={classNames(
              styles.lead,
              customStyle.themeColorFirst,
              shouldUseBlack && "should-use-black"
            )}
          >
            <div className={styles.container}>
              <div className={styles.leadBox}>
                <div className={styles.leadBoxLeft}>
                  <h2
                    className={classNames(
                      styles.sectionTitle,
                      customStyle.leadSectionTitle
                    )}
                  >
                    <span>
                      <img
                        src={IFImg(
                          shouldUseBlack
                            ? `/img/campaign/${titlePath}/lead-title-bk.svg`
                            : `/img/campaign/${titlePath}/lead-title.svg`
                        )}
                        width={95}
                        height={25}
                        alt="OVERVIEW"
                      />
                    </span>
                    <em>
                      {teamName}をスポーツバーで。
                      <br />
                      ファンがつくる、もう一つのスタジアム。
                    </em>
                  </h2>
                  <div className={classNames(styles.leadBoxRight, "md:none")}>
                    <img
                      src={IFImg("/img/campaign/common/lead-image.jpg")}
                      width={686}
                      height={457}
                      alt=""
                    />
                  </div>
                  <p className={styles.leadText}>
                    {colorText}
                    に染まったDAZN公式契約スポーツバーで熱く{teamName}
                    を応援しよう！
                    {teamName !== "横浜F・マリノス" &&
                      teamName !== "名古屋グランパス" &&
                      `2022シーズン中はオリジナル応援${drinkOrFood}も販売！`}
                    スポーツはやっぱり仲間と観ると面白い。
                  </p>
                </div>
                <div
                  className={classNames(styles.leadBoxRight, "none md:block")}
                >
                  <img
                    src={IFImg("/img/campaign/common/lead-image.jpg")}
                    width={686}
                    height={457}
                    alt=""
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        <section
          className={classNames(styles.schedule, customStyle.themeColorSecond)}
        >
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>
              <span>
                <img
                  src={IFImg("/img/campaign/common/schedule-title.svg")}
                  width={97}
                  height={25}
                  alt="SCHEDULE"
                />
              </span>
              <em>{teamName}の試合日程</em>
            </h2>

            <div className={styles.broadcastBoxWrapper}>
              {matchesData.data?.campaignMatches.map((match) => (
                <div
                  key={match.id}
                  className={classNames(
                    matchListItemStyles.broadcastBox,
                    styles.broadcastBox
                  )}
                >
                  <div
                    className={classNames(
                      matchListItemStyles.link,
                      styles.link
                    )}
                  >
                    <div
                      className={classNames(
                        matchListItemStyles.team,
                        styles.team
                      )}
                    >
                      {match.contentsHolderLogoPath && (
                        <img
                          className={classNames(
                            matchListItemStyles.contentsHolderLogo,
                            styles.contentsHolderLogo
                          )}
                          src={IFImg(match.contentsHolderLogoPath)}
                          alt="contents holder logo"
                        />
                      )}
                      <div className={matchListItemStyles.cat}>
                        <i
                          className={classNames(
                            matchListItemStyles.logo,
                            styles.logo
                          )}
                          style={{
                            backgroundImage: `url(${IFImg(
                              match.homeTeamIconPath
                            )})`,
                          }}
                        >
                          {match.homeTeamDisplayShortName}
                        </i>
                        <div
                          className={classNames(
                            matchListItemStyles.name,
                            styles.name
                          )}
                        >
                          {match.homeTeamDisplayShortName}
                        </div>
                      </div>
                      <div>
                        <div
                          className={classNames(
                            matchListItemStyles.tournamentName,
                            styles.tournamentName
                          )}
                        >
                          {match.masterTournament?.displayName}
                        </div>
                        <h3
                          className={classNames(
                            matchListItemStyles.tournamentDate,
                            styles.tournamentDate
                          )}
                        >
                          {toJpDate(match.startsAt, "date")}
                          <br />
                          {shouldShowMatchTime(match)
                            ? toJpTime(match.startsAt)
                            : "時間未定"}
                        </h3>
                      </div>
                      <div className={matchListItemStyles.cat}>
                        <i
                          className={classNames(
                            matchListItemStyles.logo,
                            styles.logo
                          )}
                          style={{
                            backgroundImage: `url(${IFImg(
                              match.awayTeamIconPath
                            )})`,
                          }}
                        >
                          {match.awayTeamDisplayShortName}
                        </i>
                        <div
                          className={classNames(
                            matchListItemStyles.name,
                            styles.name
                          )}
                        >
                          {match.awayTeamDisplayShortName}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className={styles.scheduleAttention}>
              ※DAZN配信の
              {teamName === "セレッソ大阪" ||
              teamName === "FC東京" ||
              teamName === "名古屋グランパス"
                ? "明治安田Jリーグ"
                : "明治安田Jリーグ・AFCチャンピオンズリーグ"}
              が対象になります。
            </p>
          </div>
        </section>

        <section
          id={"shops"}
          className={classNames(
            styles.shops,
            customStyle.themeColorFirst,
            shouldUseBlack && "should-use-black"
          )}
        >
          <div className={styles.container}>
            <h2
              className={classNames(
                styles.sectionTitle,
                customStyle.shopSectionTitle
              )}
            >
              <span>
                <img
                  src={IFImg(
                    shouldUseBlack
                      ? `/img/campaign/${titlePath}/shops-title-bk.svg`
                      : `/img/campaign/${titlePath}/shops-title.svg`
                  )}
                  width={113}
                  height={25}
                  alt="FANSTADIUM"
                />
              </span>
              {teamName === "浦和レッズ" ? (
                <em>
                  Fansta公認 <br className={"md:none"} />
                  {teamName} ファンスタジアム
                </em>
              ) : (
                <em>{teamName}ファンスタジアム</em>
              )}
            </h2>

            <p className={styles.shopsText}>
              ファンスタジアムはファンがつくるもう一つのスタジアム。
              <br className={"none md:block"} />
              {leagueName ? (
                <>
                  {leagueName} {teamName}の
                </>
              ) : (
                <>{teamName}の</>
              )}
              スポーツライブビューイングを楽しめるDAZN公式契約スポーツバーはこちら！
              <br className={"none md:block"} />
              店舗ページからご予約できます。
            </p>

            <div className={styles.shopsListWrapper}>
              {prefectures?.map((prefectureGroup) => (
                <div key={prefectureGroup.prefecture}>
                  <div className={styles.shopsListArea}>
                    <span>
                      <em>{prefectureGroup.prefecture}</em>
                    </span>
                  </div>
                  <ul className={styles.shopsList}>
                    {prefectureGroup.shopUuids.map((shopUuid) => {
                      const shop =
                        shopData && shopData.find((s) => s.uuid === shopUuid);
                      return (
                        shop && (
                          <li className={styles.shopsListItem} key={shop.uuid}>
                            <div className={styles.image}>
                              <a
                                href={paths.shopDetail(shop.uuid)}
                                target="_blank"
                              >
                                <img
                                  src={getImageFluxUrl({
                                    imageType: "lpShopMobile",
                                    url: shop.shopImages[0].imageUrl,
                                    alt: shop.name,
                                  })}
                                  width={180}
                                  height={269}
                                  alt={shop.name}
                                  className={"md:none"}
                                />
                                <img
                                  src={getImageFluxUrl({
                                    imageType: "lpShop",
                                    url: shop.shopImages[0].imageUrl,
                                    alt: shop.name,
                                  })}
                                  width={300}
                                  height={302}
                                  alt={shop.name}
                                  className={"none md:block"}
                                />
                              </a>
                            </div>
                            <dl className={styles.detail}>
                              <dt className={styles.detailTitle}>
                                <div className={styles.detailArea}>
                                  <span>{shop.masterArea.name}</span>
                                </div>
                                <a
                                  href={paths.shopDetail(shop.uuid)}
                                  target="_blank"
                                >
                                  {shop.name}
                                </a>
                              </dt>
                              <dd>
                                <a
                                  className={styles.detailAccess}
                                  href={createGoogleMapAppUrl({
                                    prefecture: shop.masterPrefecture.name,
                                    city: shop.city,
                                    block: shop.block,
                                    building: shop.building,
                                    name: shop.name,
                                  })}
                                  target="_blank"
                                >
                                  {shop.masterPrefecture.name}
                                  {shop.city}
                                  {shop.block}
                                  {shop.building}
                                </a>
                              </dd>
                              <dd className={styles.flex}>
                                <div>
                                  {isPcByUA ?? false ? (
                                    <div
                                      className={classNames(
                                        styles.detailTel,
                                        styles.detailTelPc
                                      )}
                                    >
                                      {shop.telWithHyphen}
                                    </div>
                                  ) : (
                                    <a
                                      className={styles.detailTel}
                                      onClick={() =>
                                        launchTel(shop.tel, "電話番号")
                                      }
                                    >
                                      {shop.telWithHyphen}
                                    </a>
                                  )}
                                </div>
                                <a
                                  href={paths.shopDetail(shop.uuid)}
                                  className={styles.detailButton}
                                  target="_blank"
                                >
                                  予約する
                                </a>
                              </dd>
                            </dl>
                          </li>
                        )
                      );
                    })}
                  </ul>
                </div>
              ))}
              <p
                className={classNames(
                  styles.shopsAttention,
                  customStyle.shopsAttentionColor
                )}
              >
                ※Fansta掲載店舗はすべてDAZN公式契約店です。
                <br />
                ※上映される試合や営業時間が変更されている場合がございます。各店舗へご確認ください。
              </p>
            </div>

            <div>
              <Link
                href={generateToShopsUrl()}
                className={classNames(
                  styles.shopsShopButton,
                  customStyle.shopsShopButton
                )}
              >
                <span>キャンペーン対象外を含む店舗一覧</span>
              </Link>
            </div>
          </div>
        </section>

        {!presentAreaHidden && (
          <section
            className={classNames(styles.present, customStyle.themeColorSecond)}
          >
            <div className={styles.container}>
              <h2
                className={classNames(
                  styles.sectionTitle,
                  customStyle.presentSectionTitle
                )}
              >
                <span>
                  {teamName === "川崎フロンターレ" ? (
                    <img
                      src={IFImg(
                        "/img/campaign/frontale-202203/present-title.svg"
                      )}
                      width={71}
                      height={25}
                      alt="FOODS"
                    />
                  ) : (
                    <img
                      src={IFImg("/img/campaign/common/present-title.svg")}
                      width={78}
                      height={25}
                      alt="DRINKS"
                    />
                  )}
                </span>
                <em>
                  売上金の一部がクラブ運営費に！
                  <br />
                  {teamName === "浦和レッズ" ? "Fanstaオリジナル " : ""}
                  {teamName}応援
                  {drinkOrFood}
                </em>
              </h2>

              {teamName === "浦和レッズ" ? (
                <p className={styles.presentText}>
                  2022シーズン中 上記店舗で販売！
                  <br />
                  浦和レッズ応援ドリンクの売上の一部は浦和レッズの活動資金として使用されます。
                </p>
              ) : (
                <p className={styles.presentText}>
                  2022シーズン中 上記店舗で販売！
                  <br />
                  売上金の一部がクラブ運営費に充てられます。チームの強化をサポートしよう！
                </p>
              )}

              <div className={styles.presentImage}>
                <span className="md:none">
                  <img
                    src={IFImg(`/img/campaign/${slug}/sp/present-image.png`)}
                    width={686}
                    height={291}
                    alt={`応援${drinkOrFood}を${
                      teamName === "川崎フロンターレ" ? "食べて" : "飲んで"
                    }楽しく応援 購入者限定プレゼント${
                      teamName === "浦和レッズ" ? "Fanstaオリジナル" : ""
                    }ステッカー貰える！ ※画像はイメージです。`}
                  />
                </span>
                <span className="none md:block">
                  <img
                    src={IFImg(`/img/campaign/${slug}/pc/present-image.png`)}
                    width={1888}
                    height={801}
                    alt={`応援${drinkOrFood}を${
                      teamName === "川崎フロンターレ" ? "食べて" : "飲んで"
                    }楽しく応援 購入者限定プレゼント${
                      teamName === "浦和レッズ" ? "Fanstaオリジナル" : ""
                    }ステッカー貰える！ ※画像はイメージです。`}
                  />
                </span>
              </div>

              <dl className={styles.presentAttention}>
                <dt>
                  <span className={styles.label}>
                    <em>
                      応援
                      {drinkOrFood}
                      について
                    </em>
                  </span>
                </dt>
                <dd>
                  <ul>
                    <li>
                      2022年4月1日(金)から2022シーズン終了まで、本ページ記載の店舗にて販売します。
                    </li>
                    <li>
                      店舗によって
                      {drinkOrFood}
                      の内容、種類、価格が異なります。詳細は各店舗にお問い合わせください。
                    </li>
                    <li>
                      商品はなくなり次第終了となります。また、商品の内容は変更となる場合があります。
                    </li>
                  </ul>
                </dd>
              </dl>
            </div>
          </section>
        )}

        {teamName === "セレッソ大阪" && (
          <section
            className={classNames(styles.present, customStyle.themeColorFirst)}
          >
            <div className={styles.container}>
              <h2
                className={classNames(
                  styles.sectionTitle,
                  customStyle.presentSectionTitle
                )}
              >
                <span>
                  <img
                    src={IFImg(`/img/campaign/${slug}/food-title.svg`)}
                    width={71}
                    height={25}
                    alt="FOODS"
                  />
                </span>
                <em>
                  ニッポンハム×セレッソ大阪×Fansta
                  <br />
                  ニッポンハム製品を食べながら美味しく楽しく応援しよう！
                </em>
              </h2>

              <p className={styles.presentText}>
                2022シーズン終了まで上記店舗で販売中！
                <br />
                応援ドリンクと一緒に注文すると、売上金の一部がクラブ運営費に充てられます。チームの強化をサポートしよう！
              </p>

              <div className={styles.presentImage}>
                <span className="md:none">
                  <img
                    src={IFImg(`/img/campaign/${slug}/sp/food-image.png`)}
                    width={686}
                    height={2210}
                    alt={"応援フードを食べて楽しく応援 ※画像はイメージです。"}
                  />
                </span>
                <span className="none md:block">
                  <img
                    src={IFImg(`/img/campaign/${slug}/pc/food-image.png`)}
                    width={1888}
                    height={1826}
                    alt={"応援フードを食べて楽しく応援 ※画像はイメージです。"}
                  />
                </span>
              </div>

              <dl className={styles.presentAttention}>
                <dt>
                  <span className={styles.label}>
                    <em>応援ドリンクセットについて</em>
                  </span>
                </dt>
                <dd>
                  <ul>
                    <li>
                      各店舗で応援ドリンクとのセット・単品販売を致します。セットドリンク・価格などの詳細は各店舗でご確認ください。
                    </li>
                  </ul>
                </dd>
              </dl>
            </div>
          </section>
        )}

        {teamName === "浦和レッズ" && (
          <div className={styles.contact}>
            <div className={styles.container}>
              <div className={styles.contactButton}>
                Fanstaサービス内の浦和レッズの商標（エンブレム等）は浦和レッドダイヤモンズ株式会社に帰属します。本キャンペーンについてのお問い合わせは、
                <Link href="https://support.fansta.jp/hc/ja/requests/new">
                  Fanstaお問い合わせ窓口
                </Link>
                までお願いいたします。
              </div>
            </div>
          </div>
        )}

        <AppLinkSection customStyle={customStyle.appLinkSection} />

        <section
          className={classNames(styles.fanstaShop, customStyle.fanstaShop)}
        >
          <div className={styles.container}>
            <h2 className={styles.fanstaShopTitle}>
              <span>
                <em>掲載ご希望のお店の方へ</em>
              </span>
            </h2>

            <div className={styles.fanstaShopImage}>
              <span className="md:none">
                <img
                  src={IFImg(`/img/campaign/common/sp/shop-offer.svg`)}
                  width={343}
                  height={243}
                  alt=""
                />
              </span>
              <span className="none md:block">
                <img
                  src={IFImg(`/img/campaign/common/pc/shop-offer.svg`)}
                  width={560}
                  height={269}
                  alt=""
                />
              </span>
            </div>

            <p className={styles.fanstaShopText}>
              Fanstaへの掲載・資料請求をご希望の店舗様は以下からお問い合わせください。
            </p>

            <div>
              <Link
                href={paths.contact}
                className={styles.fanstaShopButton}
                id={"contact-link-" + slug}
              >
                <span>掲載のお問い合わせはこちら</span>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CampaignTemplate;
